import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { supabase } from './supabaseClient';

export interface SignUpInput {
  email: string;
  password: string;
  username: string;
  first_name?: string;
  last_name?: string;
  street_address?: string;
  city?: string;
  region?: string;
  postal_code?: string;
  country_code?: string;
}

// Base pubblica per i link via email (conferma registrazione, recupero password):
// su mobile NON si può usare window.location.origin (= https://localhost,
// irraggiungibile dal browser di sistema dopo la verifica del token) → si usa il
// dominio web pubblico, che serve la stessa SPA con la rotta /auth/callback.
// Su web resta l'origin reale del deployment.
const PUBLIC_WEB_URL = (import.meta.env.VITE_PUBLIC_WEB_URL as string | undefined) ?? 'https://www.swapwishtcg.com';

function authCallbackUrl(query: Record<string, string> = {}): string {
  const origin = Capacitor.isNativePlatform() ? PUBLIC_WEB_URL : window.location.origin;
  const appBase = new URL(import.meta.env.BASE_URL || '/', origin);
  const callback = new URL('auth/callback', appBase);
  for (const [k, v] of Object.entries(query)) {
    callback.searchParams.set(k, v);
  }
  return callback.toString();
}

export const authService = {
  async signUp({
    email, password, username,
    first_name, last_name, street_address, city, region, postal_code, country_code,
  }: SignUpInput) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // type=signup: il callback (browser) mostra la vista "registrazione
        // confermata, torna nell'app" invece di tentare l'accesso nel browser.
        emailRedirectTo: authCallbackUrl({ type: 'signup' }),
        data: {
          username,
          first_name: first_name ?? null,
          last_name: last_name ?? null,
          street_address: street_address ?? null,
          city: city ?? null,
          region: region ?? null,
          postal_code: postal_code ?? null,
          country_code: country_code ?? null,
        },
      },
    });
    if (error) throw error;

    // Offuscamento anti-enumerazione di Supabase: per un'email GIÀ registrata
    // signUp ritorna 200 con un user fittizio e `identities` VUOTO, e NON invia
    // alcuna mail di conferma. Lo intercettiamo per dare un messaggio chiaro
    // ("email già in uso") invece del fuorviante "controlla la mail": è il caso
    // tipico di un indirizzo già collegato a Google. Vedi LoginView.submit().
    if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      const err = new Error('email_already_registered') as Error & { code?: string };
      err.code = 'email_already_registered';
      throw err;
    }

    // After signup, update the profiles row with personal fields via RPC/direct update.
    // The profiles row is created by the Supabase trigger on auth.users insert;
    // we patch the extra fields immediately after signup.
    if (data.user) {
      const patch: Record<string, unknown> = {};
      if (first_name)     patch.first_name = first_name;
      if (last_name)      patch.last_name = last_name;
      if (street_address) patch.street_address = street_address;
      if (city)           patch.city = city;
      if (region)         patch.region = region;
      if (postal_code)    patch.postal_code = postal_code;
      if (country_code)   patch.country_code = country_code;
      // preferred_game_slug resta NULL fino alla scelta effettiva nel primo
      // step dell'onboarding; preimpostarlo qui farebbe saltare l'onboarding.
      // Swallow errors: profile may not exist yet if email confirmation is required.
      if (Object.keys(patch).length > 0) {
        await supabase.from('profiles').update(patch).eq('id', data.user.id);
      }
    }

    return data;
  },

  /**
   * Pre-check di disponibilità dello username (RPC is_username_available,
   * unicità case-insensitive; a utente autenticato esclude se stesso, così il
   * cambio di sole maiuscole del proprio username resta permesso).
   * In caso di errore RPC NON blocca il flusso (ritorna true): la registrazione
   * resta comunque protetta dal fallback con suffisso in handle_new_user e la
   * modifica profilo dalla violazione unique (23505) gestita dal chiamante.
   */
  async isUsernameAvailable(username: string): Promise<boolean> {
    const candidate = String(username ?? '').trim();
    if (!candidate) return false;
    const { data, error } = await supabase.rpc('is_username_available', { candidate });
    if (error) {
      console.warn('[isUsernameAvailable]', error);
      return true;
    }
    return data === true;
  },

  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  },

  async signInWithGoogle() {
    // Su mobile l'OAuth via redirect nel WebView è bloccato da Google
    // ("disallowed_useragent"): si usa il flusso OAuth gestito da Supabase
    // aprendo l'URL nel BROWSER DI SISTEMA (Custom Tab/SFSafariViewController).
    // Al termine Google → callback Supabase → redirect all'App Link
    // (https://www.swapwishtcg.com/auth/callback): lo intercetta App.vue
    // (appUrlOpen → exchangeCodeForSession) che crea la sessione. Nessun plugin
    // nativo, nessun serverClientId/SHA-1 lato sign-in.
    if (Capacitor.isNativePlatform()) {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: authCallbackUrl(),
          skipBrowserRedirect: true,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      });
      if (error) {
        console.error('[signInWithGoogle] signInWithOAuth (native) failed', error);
        throw error;
      }
      if (data?.url) await Browser.open({ url: data.url, presentationStyle: 'popover' });
      // La sessione NON è pronta qui: arriva via deep-link (App.vue). Il watcher
      // su userStore.user in LoginView gestisce poi la navigazione a /loading.
      return data;
    }

    // Web: redirect OAuth standard (il browser gestisce il flusso).
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: authCallbackUrl(),
        queryParams: { access_type: 'offline', prompt: 'consent' },
      },
    });
    if (error) throw error;
    return data;
  },

  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  /**
   * Elimina definitivamente l'account e tutti i dati associati tramite la
   * edge function `delete-account` (service role). Dopo l'eliminazione
   * pulisce la sola sessione locale: l'utente non esiste più lato server.
   */
  async deleteAccount() {
    const { data, error } = await supabase.functions.invoke('delete-account', { body: {} });
    if (error) {
      // Le risposte non-2xx arrivano come FunctionsHttpError: il payload JSON
      // (es. code 'subscription_cancel_failed') è leggibile da error.context.
      const response = (error as { context?: Response })?.context;
      if (response && typeof response.json === 'function') {
        const payload = await response.json().catch(() => null) as { error?: string; code?: string } | null;
        if (payload?.code) {
          const err = new Error(String(payload.error ?? 'Account deletion failed')) as Error & { code?: string };
          err.code = payload.code;
          throw err;
        }
      }
      throw error;
    }
    if (!(data as { ok?: boolean } | null)?.ok) {
      throw new Error(String((data as { error?: string } | null)?.error ?? 'Account deletion failed'));
    }
    await supabase.auth.signOut({ scope: 'local' }).catch(() => { });
  },

  async resetPassword(email: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: authCallbackUrl({ type: 'recovery' }),
    });
    if (error) throw error;
  },

  // Imposta la nuova password DOPO che il link di recupero ha creato la sessione
  // di recovery (gestita da AuthCallbackView → /auth/reset-password).
  async updatePassword(newPassword: string) {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  },

  async getSession() {
    const { data } = await supabase.auth.getSession();
    return data.session;
  },

  async getUser() {
    const { data } = await supabase.auth.getUser();
    return data.user;
  },

  onAuthChange(cb: (event: string, session: unknown) => void) {
    return supabase.auth.onAuthStateChange(cb);
  },
};
